// This hook was created purely to match the figma design
// which has the last question with a different bottom margin than the rest of the questions.
// It calculates the height of the last question card and sets the bottom margin such
// that the card is always fully visible above the fold.
// This is achieved by using a ResizeObserver to watch for changes in the card's
// height and updating the bottom margin accordingly.
// The formula to apply the margin is 100vh - card height - 134px (the height of the header and some padding)

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/** Height of the sticky header + surrounding padding (px) */
export const HEADER_AND_PADDING_OFFSET_PX = 134;

interface UseLastQuestionMarginParams {
  ref: React.ForwardedRef<HTMLDivElement>;
  isLastQuestion?: boolean;
  isLastPage?: boolean;
  isSkipped: boolean;
  formValue?: String[] | string;
  type: string;
  questionText?: string;
  showNextButton?: boolean;
}

export const useLastQuestionMargin = ({
  ref,
  isLastQuestion,
  isLastPage,
  isSkipped,
  formValue,
  type,
  questionText,
  showNextButton,
}: UseLastQuestionMarginParams) => {
  const [cardHeight, setCardHeight] = useState(0);
  const cardRef = useRef<HTMLDivElement | null>(null);

  const setCardRef = useCallback(
    (node: HTMLDivElement | null) => {
      cardRef.current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        Object.assign(ref, { current: node });
      }
    },
    [ref],
  );

  useEffect(() => {
    if (!isLastQuestion) {
      return undefined;
    }

    const updateCardHeight = () => {
      if (!cardRef.current) {
        return;
      }

      requestAnimationFrame(() => {
        if (cardRef.current) {
          const { height } = cardRef.current.getBoundingClientRect();
          setCardHeight(height);
        }
      });
    };

    updateCardHeight();
    const timer = setTimeout(updateCardHeight, 250);

    const resizeObserver = new ResizeObserver(() => {
      updateCardHeight();
    });

    if (cardRef.current) {
      resizeObserver.observe(cardRef.current);
    }

    return () => {
      clearTimeout(timer);
      resizeObserver.disconnect();
    };
  }, [isLastQuestion, isSkipped, formValue, type, questionText, showNextButton]);

  const bottomMargin = useMemo(() => {
    if (!isLastQuestion) return undefined;
    if (cardHeight === 0) return '0px';
    const offset = HEADER_AND_PADDING_OFFSET_PX;
    return `calc(100vh - ${cardHeight}px - ${offset}px)`;
  }, [isLastQuestion, isLastPage, cardHeight]);

  return { setCardRef, bottomMargin };
};
